/**
 * Backend accept-invite API — bridges an invited teammate on their first
 * authenticated request (B2).
 *
 * Called from /auth/reset-password AFTER the invitee sets their password, when
 * the Supabase session carries the invite breadcrumb
 * (`user_metadata.elreign_user_doc_id`). The handler lives on the
 * tenant-onboarding FA (the one with the Supabase service-role key): it links
 * the Supabase identity to the pre-created Cosmos UserDoc, stamps
 * app_metadata.{tenant_id, role}, and reconciles trust memberIds. The SPA then
 * refreshSession()s (to pick up the freshly-stamped tenant_id claim) and routes
 * into the app.
 *
 * Mirrors the shape + posture of `onboard.ts`.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';
import type { UserRole } from './onboard';

export interface AcceptInviteProfile {
  userId: string;
  tenantId: string;
  role: UserRole;
  displayName: string;
  /** Trust ids whose memberIds now include this user (rm assignees). */
  linkedTrustIds: string[];
}

export interface AcceptInviteResponse {
  accepted: true;
  /** False when the link already existed (double-submit / SPA retry). */
  firstAcceptance: boolean;
  profile: AcceptInviteProfile;
  /** Present when the dev FA has no SUPABASE_SERVICE_ROLE_KEY — the Cosmos
   *  link landed but app_metadata was not stamped, so the user must refresh
   *  their session manually before subsequent protected calls see tenant_id. */
  noticeFlags?: { supabaseAdminSkipped?: boolean };
  correlationId: string;
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  // Body-less POST — the JWT carries every input (the invite breadcrumbs live
  // in user_metadata). We pass `void` so callers `mutate()` / `mutateAsync()`
  // with no argument.
  return useMutation<AcceptInviteResponse, ApiError, void>({
    mutationFn: () =>
      api<AcceptInviteResponse>('/api/v1/auth/accept-invite', {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trusts'] });
      void qc.invalidateQueries({ queryKey: ['users'] });
      void qc.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
}
